/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client';

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useNavigate } from '@/hooks/use-navigate';
import { useAnalytics } from '@/hooks/use-analytics';
import { hasPurchaseBeenTracked, markPurchaseTracked, validatedNativePurchase } from '@/analytics/purchase-event';
import { routeHref, routes } from '@/route-paths';
import { cn } from '@/lib/utils';
import {
    buildApplePayBridgeUrl,
    completedOrderNumber,
    type ApplePayBridgeContext,
} from '../lib/native-apple-pay-bridge';
import { completeNativeCartApplePay } from '../lib/native-apple-pay-client';

interface Props {
    context: ApplePayBridgeContext;
    sku?: string;
    handoffToken?: string;
    className?: string;
    fallback?: ReactNode;
}

interface ApplePayCapabilityWindow extends Window {
    ApplePaySession?: {
        canMakePayments(): boolean;
    };
}

const completionHref = (_context: ApplePayBridgeContext, orderNo: string) =>
    routeHref(routes.applePayConfirmation, {
        orderNo,
    });

/**
 * Hosts SFCC's native `<isapplepay>` output in a same-origin document. SFCC owns merchant
 * validation, Apple Pay callbacks, Cybersource authorization, and order placement; this component
 * deliberately never receives Apple credentials or payment-token data.
 */
export default function ApplePayButton({ context, sku, handoffToken, className, fallback }: Props) {
    const { site, locale, currency } = useSite();
    const navigate = useNavigate();
    const analytics = useAnalytics();
    const [browserEligible, setBrowserEligible] = useState(false);
    const [bridgeReady, setBridgeReady] = useState(false);
    const [completionPending, setCompletionPending] = useState(false);
    const completed = useRef(false);
    const buttonPresented = useRef(false);
    const sheetActivated = useRef(false);
    const sheetBlurred = useRef(false);
    const navigationStarted = useRef(false);
    const frameRef = useRef<HTMLIFrameElement | null>(null);
    const readinessObserver = useRef<MutationObserver | null>(null);
    const removeNavigationListener = useRef<(() => void) | null>(null);
    const activationTimer = useRef<number | null>(null);
    const recoveryTimer = useRef<number | null>(null);
    const src = useMemo(
        () => buildApplePayBridgeUrl(site.id, locale.id, context, sku, handoffToken),
        [context, handoffToken, locale.id, site.id, sku]
    );
    useEffect(() => {
        try {
            const ApplePaySession = (window as ApplePayCapabilityWindow).ApplePaySession;
            setBrowserEligible(Boolean(window.isSecureContext && ApplePaySession?.canMakePayments()));
        } catch {
            setBrowserEligible(false);
        }
    }, []);

    useEffect(() => {
        readinessObserver.current?.disconnect();
        readinessObserver.current = null;
        removeNavigationListener.current?.();
        removeNavigationListener.current = null;
        setBridgeReady(false);
        setCompletionPending(false);
        completed.current = false;
        buttonPresented.current = false;
        sheetActivated.current = false;
        sheetBlurred.current = false;
        navigationStarted.current = false;
        if (activationTimer.current !== null) window.clearTimeout(activationTimer.current);
        if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
        activationTimer.current = null;
        recoveryTimer.current = null;

        return () => {
            readinessObserver.current?.disconnect();
            readinessObserver.current = null;
            removeNavigationListener.current?.();
            removeNavigationListener.current = null;
            if (activationTimer.current !== null) window.clearTimeout(activationTimer.current);
            if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
        };
    }, [src]);

    useEffect(() => {
        const handleCompletion = (event: MessageEvent<unknown>) => {
            const frame = frameRef.current;
            if (
                !frame ||
                event.origin !== window.location.origin ||
                event.source !== frame.contentWindow ||
                !event.data ||
                typeof event.data !== 'object'
            ) {
                return;
            }

            const message = event.data as {
                type?: unknown;
                orderNo?: unknown;
                purchase?: unknown;
                handoffToken?: unknown;
            };
            if (
                message.type !== 'sfn-native-apple-pay-complete' ||
                typeof message.orderNo !== 'string' ||
                !/^[A-Za-z0-9_-]{1,64}$/.test(message.orderNo) ||
                (context === 'cart' && message.handoffToken !== handoffToken) ||
                completed.current
            ) {
                return;
            }

            completed.current = true;
            navigationStarted.current = true;
            if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
            frame.style.visibility = 'hidden';
            setBridgeReady(false);
            setCompletionPending(true);
            if (!hasPurchaseBeenTracked(message.orderNo)) {
                const purchase = validatedNativePurchase(message.purchase, message.orderNo);
                if (purchase) {
                    // Match the standard confirmation route: mark synchronously before the async
                    // adapter call so re-render/navigation cannot double-count the transaction.
                    markPurchaseTracked(message.orderNo);
                    void analytics.trackPurchase({ nativeDataLayerEvent: purchase, nativeOrderNo: message.orderNo });
                }
            }
            const finalize = async () => {
                if (context === 'cart' && handoffToken) {
                    try {
                        await completeNativeCartApplePay(handoffToken, message.orderNo as string);
                    } catch {
                        // The order is already placed. Preserve the confirmation experience while
                        // leaving a changed or failed-to-delete SCAPI basket intact for safe recovery.
                    }
                }
                await navigate(completionHref(context, message.orderNo as string));
            };
            void finalize();
        };

        window.addEventListener('message', handleCompletion);
        return () => window.removeEventListener('message', handleCompletion);
    }, [analytics, context, handoffToken, navigate]);

    if (!browserEligible || currency !== 'USD' || (context === 'pdp' && !sku) || (context === 'cart' && !handoffToken))
        return fallback ?? null;

    const bridgeVisible = bridgeReady || completionPending;
    return (
        <div
            aria-hidden={!bridgeVisible && !fallback}
            inert={!bridgeVisible && !fallback}
            className={cn(
                className,
                bridgeVisible || fallback
                    ? 'relative block'
                    : 'pointer-events-none fixed -left-[10000px] top-0 w-80 opacity-0'
            )}>
            <iframe
                ref={frameRef}
                src={src}
                title={context === 'pdp' ? 'Buy with Apple Pay' : 'Pay with Apple Pay'}
                allow="payment"
                referrerPolicy="same-origin"
                aria-hidden={!bridgeVisible}
                inert={!bridgeVisible}
                className={cn(
                    'h-11 min-h-11 w-full border-0',
                    !bridgeVisible && fallback && 'pointer-events-none fixed -left-[10000px] top-0 w-80 opacity-0'
                )}
                data-testid="cybersource-native-apple-pay-bridge"
                onLoad={(event) => {
                    const frame = event.currentTarget;
                    if (buttonPresented.current) {
                        // Every navigation after activation starts covered. Only the original native
                        // button document or a verified completion message may change parent state.
                        frame.style.visibility = 'hidden';
                        setBridgeReady(false);
                        setCompletionPending(true);
                    }
                    readinessObserver.current?.disconnect();
                    readinessObserver.current = null;
                    removeNavigationListener.current?.();
                    removeNavigationListener.current = null;
                    try {
                        const frameWindow = frame.contentWindow;
                        if (!frameWindow) {
                            setBridgeReady(false);
                            return;
                        }
                        const frameDocument = frameWindow.document;

                        const restoreNativeButton = () => {
                            if (completed.current || navigationStarted.current) return;
                            const currentDocument = frame.contentWindow?.document;
                            const buttonAvailable = Boolean(
                                currentDocument === frameDocument &&
                                    currentDocument.querySelector('.dw-apple-pay-button')
                            );
                            if (!buttonAvailable) {
                                // The bridge has left its native button document. Keep it covered so
                                // an SFRA confirmation, error page, or storefront chrome cannot paint.
                                frame.style.visibility = 'hidden';
                                setBridgeReady(false);
                                setCompletionPending(true);
                                return;
                            }
                            sheetActivated.current = false;
                            sheetBlurred.current = false;
                            frame.style.visibility = '';
                            setCompletionPending(false);
                            setBridgeReady(true);
                        };

                        const handleApplePayActivation = (clickEvent: Event) => {
                            const target = clickEvent.target as {
                                closest?: (selector: string) => Element | null;
                            } | null;
                            if (!target?.closest?.('.dw-apple-pay-button')) return;

                            sheetActivated.current = true;
                            sheetBlurred.current = false;
                            navigationStarted.current = false;
                            if (activationTimer.current !== null) window.clearTimeout(activationTimer.current);
                            if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);

                            // Defer until the originating click finishes so SFCC's opaque native
                            // handler can synchronously create and begin the ApplePaySession.
                            activationTimer.current = window.setTimeout(() => {
                                if (completed.current || navigationStarted.current) return;
                                frame.style.visibility = 'hidden';
                                setBridgeReady(false);
                                setCompletionPending(true);
                                // Focus recovery handles normal cancellation. This is a final guard
                                // for browser versions that do not emit blur/focus around the sheet.
                                recoveryTimer.current = window.setTimeout(restoreNativeButton, 60000);
                            }, 0);
                        };

                        const handleWindowBlur = () => {
                            if (sheetActivated.current) sheetBlurred.current = true;
                        };
                        const handleWindowFocus = () => {
                            if (!sheetActivated.current || !sheetBlurred.current || navigationStarted.current) return;
                            if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
                            // Successful authorization begins iframe navigation immediately. The
                            // short delay lets beforeunload win; cancellation instead restores retry.
                            recoveryTimer.current = window.setTimeout(restoreNativeButton, 300);
                        };
                        if (typeof frameDocument.addEventListener === 'function') {
                            frameDocument.addEventListener('click', handleApplePayActivation, true);
                        }
                        window.addEventListener('blur', handleWindowBlur);
                        window.addEventListener('focus', handleWindowFocus);

                        const hideBeforeNavigation = () => {
                            // Hide synchronously so SFRA confirmation markup cannot paint while React
                            // transitions the parent document to Storefront Next confirmation.
                            frame.style.visibility = 'hidden';
                            navigationStarted.current = true;
                            if (activationTimer.current !== null) window.clearTimeout(activationTimer.current);
                            if (recoveryTimer.current !== null) window.clearTimeout(recoveryTimer.current);
                            setBridgeReady(false);
                            if (buttonPresented.current) setCompletionPending(true);
                            readinessObserver.current?.disconnect();
                            readinessObserver.current = null;
                        };
                        if (typeof frameWindow.addEventListener === 'function') {
                            frameWindow.addEventListener('beforeunload', hideBeforeNavigation);
                        }
                        removeNavigationListener.current = () => {
                            if (typeof frameWindow.removeEventListener === 'function') {
                                try {
                                    frameWindow.removeEventListener('beforeunload', hideBeforeNavigation);
                                } catch {
                                    // The old iframe document may already have navigated away.
                                }
                            }
                            if (typeof frameDocument.removeEventListener === 'function') {
                                frameDocument.removeEventListener('click', handleApplePayActivation, true);
                            }
                            window.removeEventListener('blur', handleWindowBlur);
                            window.removeEventListener('focus', handleWindowFocus);
                        };

                        const orderNo = completedOrderNumber(frameWindow.location.href, window.location.origin);
                        if (context === 'pdp' && orderNo && !completed.current) {
                            completed.current = true;
                            setBridgeReady(false);
                            setCompletionPending(true);
                            void navigate(completionHref(context, orderNo));
                            return;
                        }
                        if (frameDocument.querySelector('.dw-apple-pay-button')) {
                            frame.style.visibility = '';
                            buttonPresented.current = true;
                            setCompletionPending(false);
                            setBridgeReady(true);
                            return;
                        }

                        setBridgeReady(false);
                        const observer = new MutationObserver(() => {
                            if (!frameDocument.querySelector('.dw-apple-pay-button')) return;

                            frame.style.visibility = '';
                            buttonPresented.current = true;
                            setCompletionPending(false);
                            setBridgeReady(true);
                            observer.disconnect();
                            if (readinessObserver.current === observer) readinessObserver.current = null;
                        });
                        observer.observe(frameDocument.documentElement, { childList: true, subtree: true });
                        readinessObserver.current = observer;
                    } catch {
                        // The bridge must remain same-origin. Fail closed if routing sends it elsewhere.
                        setBridgeReady(false);
                    }
                }}
            />
            {completionPending ? (
                <div
                    role="status"
                    aria-label="Processing Apple Pay order"
                    className="absolute inset-0 z-10 flex h-11 min-h-11 items-center justify-center bg-foreground">
                    <span
                        aria-hidden="true"
                        className="size-5 animate-spin rounded-full border-2 border-background/40 border-t-background"
                    />
                </div>
            ) : null}
            {!bridgeReady && !completionPending ? fallback : null}
        </div>
    );
}
